import { base64UrlToBytes, bytesToBase64Url } from "./crypto";

export const PRIVATE_STORAGE_DB_NAME = "feel-scene-private-v1";
export const PRIVATE_STORAGE_KEY_STORE = "keys";
export const PRIVATE_STORAGE_RECORD_STORE = "records";
export const PRIVATE_STORAGE_KEY_ID = "device-key-v1";

const DATABASE_VERSION = 1;
const RECORD_VERSION = 1;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

interface PrivateRecord {
  id: string;
  version: typeof RECORD_VERSION;
  expiresAt: number;
  iv: string;
  ciphertext: string;
}

let databasePromise: Promise<IDBDatabase> | null = null;
let deviceKeyPromise: Promise<CryptoKey> | null = null;
const recordOperations = new Map<string, Promise<unknown>>();

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), {
      once: true,
    });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("Browser storage request failed.")),
      { once: true },
    );
  });
}

function transactionComplete(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () =>
        reject(
          transaction.error ?? new Error("Browser storage transaction aborted."),
        ),
      { once: true },
    );
    transaction.addEventListener(
      "error",
      () =>
        reject(
          transaction.error ?? new Error("Browser storage transaction failed."),
        ),
      { once: true },
    );
  });
}

function openDatabase() {
  if (databasePromise) return databasePromise;
  databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(PRIVATE_STORAGE_DB_NAME, DATABASE_VERSION);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(PRIVATE_STORAGE_KEY_STORE)) {
        database.createObjectStore(PRIVATE_STORAGE_KEY_STORE);
      }
      if (!database.objectStoreNames.contains(PRIVATE_STORAGE_RECORD_STORE)) {
        database.createObjectStore(PRIVATE_STORAGE_RECORD_STORE, {
          keyPath: "id",
        });
      }
    });
    request.addEventListener("success", () => {
      const database = request.result;
      database.addEventListener("versionchange", () => {
        database.close();
        databasePromise = null;
        deviceKeyPromise = null;
      });
      resolve(database);
    });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("Private storage is unavailable.")),
      { once: true },
    );
    request.addEventListener(
      "blocked",
      () => reject(new Error("Private storage upgrade is blocked.")),
      { once: true },
    );
  }).catch((error) => {
    databasePromise = null;
    throw error;
  });
  return databasePromise;
}

async function readStoredKey(database: IDBDatabase) {
  const transaction = database.transaction(
    PRIVATE_STORAGE_KEY_STORE,
    "readonly",
  );
  const completed = transactionComplete(transaction);
  const [value] = await Promise.all([
    requestResult(
      transaction
        .objectStore(PRIVATE_STORAGE_KEY_STORE)
        .get(PRIVATE_STORAGE_KEY_ID),
    ),
    completed,
  ]);
  return value as CryptoKey | undefined;
}

function isDeviceKey(value: CryptoKey | undefined): value is CryptoKey {
  return Boolean(
    value &&
      value.type === "secret" &&
      value.extractable === false &&
      value.algorithm.name === "AES-GCM" &&
      value.usages.includes("encrypt") &&
      value.usages.includes("decrypt"),
  );
}

async function addStoredKey(database: IDBDatabase, key: CryptoKey) {
  const transaction = database.transaction(
    PRIVATE_STORAGE_KEY_STORE,
    "readwrite",
  );
  const completed = transactionComplete(transaction);
  await Promise.all([
    requestResult(
      transaction
        .objectStore(PRIVATE_STORAGE_KEY_STORE)
        .add(key, PRIVATE_STORAGE_KEY_ID),
    ),
    completed,
  ]);
}

async function getDeviceKey() {
  if (deviceKeyPromise) return deviceKeyPromise;
  deviceKeyPromise = (async () => {
    const database = await openDatabase();
    const existing = await readStoredKey(database);
    if (isDeviceKey(existing)) return existing;
    if (existing) {
      throw new Error("The browser contains an unusable private storage key.");
    }

    const generated = (await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    )) as CryptoKey;
    try {
      await addStoredKey(database, generated);
      return generated;
    } catch (error) {
      // Another tab may have created the origin key at the same time.
      const concurrent = await readStoredKey(database);
      if (isDeviceKey(concurrent)) return concurrent;
      throw error;
    }
  })().catch((error) => {
    deviceKeyPromise = null;
    throw error;
  });
  return deviceKeyPromise;
}

function associatedData(id: string, expiresAt: number) {
  return encoder.encode(
    `feel-scene-private:${RECORD_VERSION}:${id}:${expiresAt}`,
  );
}

function queueRecordOperation<T>(id: string, operation: () => Promise<T>) {
  const previous = recordOperations.get(id) ?? Promise.resolve();
  const current = previous.catch(() => undefined).then(operation);
  recordOperations.set(id, current);
  void current.then(
    () => {
      if (recordOperations.get(id) === current) recordOperations.delete(id);
    },
    () => {
      if (recordOperations.get(id) === current) recordOperations.delete(id);
    },
  );
  return current;
}

async function writeRecord(record: PrivateRecord) {
  const database = await openDatabase();
  const transaction = database.transaction(
    PRIVATE_STORAGE_RECORD_STORE,
    "readwrite",
  );
  const completed = transactionComplete(transaction);
  await Promise.all([
    requestResult(
      transaction.objectStore(PRIVATE_STORAGE_RECORD_STORE).put(record),
    ),
    completed,
  ]);
}

async function readRecord(id: string) {
  const database = await openDatabase();
  const transaction = database.transaction(
    PRIVATE_STORAGE_RECORD_STORE,
    "readonly",
  );
  const completed = transactionComplete(transaction);
  const [value] = await Promise.all([
    requestResult(
      transaction.objectStore(PRIVATE_STORAGE_RECORD_STORE).get(id),
    ),
    completed,
  ]);
  return value as PrivateRecord | undefined;
}

async function deleteRecord(id: string) {
  const database = await openDatabase();
  const transaction = database.transaction(
    PRIVATE_STORAGE_RECORD_STORE,
    "readwrite",
  );
  const completed = transactionComplete(transaction);
  transaction.objectStore(PRIVATE_STORAGE_RECORD_STORE).delete(id);
  await completed;
}

export function setPrivateValue(
  id: string,
  value: unknown,
  expiresAt: number,
) {
  return queueRecordOperation(id, async () => {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        additionalData: associatedData(id, expiresAt),
      },
      await getDeviceKey(),
      encoder.encode(JSON.stringify(value)),
    );
    await writeRecord({
      id,
      version: RECORD_VERSION,
      expiresAt,
      iv: bytesToBase64Url(iv),
      ciphertext: bytesToBase64Url(new Uint8Array(encrypted)),
    });
  });
}

export function getPrivateValue<T>(id: string, allowExpired = false) {
  return queueRecordOperation(id, async (): Promise<T | null> => {
    const record = await readRecord(id);
    if (!record) return null;
    if (
      record.version !== RECORD_VERSION ||
      !Number.isFinite(record.expiresAt) ||
      typeof record.iv !== "string" ||
      typeof record.ciphertext !== "string"
    ) {
      await deleteRecord(id);
      return null;
    }
    if (!allowExpired && record.expiresAt <= Date.now()) {
      await deleteRecord(id);
      return null;
    }
    try {
      const decrypted = await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: base64UrlToBytes(record.iv),
          additionalData: associatedData(id, record.expiresAt),
        },
        await getDeviceKey(),
        base64UrlToBytes(record.ciphertext),
      );
      return JSON.parse(decoder.decode(decrypted)) as T;
    } catch {
      await deleteRecord(id);
      return null;
    }
  });
}

export function deletePrivateValue(id: string) {
  return queueRecordOperation(id, () => deleteRecord(id));
}

export async function deletePrivateValuesByPrefixAndSuffix(
  prefix: string,
  suffix: string,
) {
  return deletePrivateValuesByPrefixWhere(
    prefix,
    (id) => id.endsWith(suffix),
  );
}

export async function deletePrivateValuesByPrefixWhere(
  prefix: string,
  matches: (id: string) => boolean,
) {
  await Promise.all(
    [...recordOperations.entries()]
      .filter(([id]) => id.startsWith(prefix) && matches(id))
      .map(([, operation]) => operation.catch(() => undefined)),
  );
  const database = await openDatabase();
  const transaction = database.transaction(
    PRIVATE_STORAGE_RECORD_STORE,
    "readonly",
  );
  const completed = transactionComplete(transaction);
  const [keys] = await Promise.all([
    requestResult(
      transaction.objectStore(PRIVATE_STORAGE_RECORD_STORE).getAllKeys(),
    ) as Promise<IDBValidKey[]>,
    completed,
  ]);
  await Promise.all(
    keys
      .filter(
        (key): key is string =>
          typeof key === "string" && key.startsWith(prefix) && matches(key),
      )
      .map(deletePrivateValue),
  );
}

export async function pruneExpiredPrivateValues(prefix: string) {
  const database = await openDatabase();
  const transaction = database.transaction(
    PRIVATE_STORAGE_RECORD_STORE,
    "readonly",
  );
  const completed = transactionComplete(transaction);
  const [records] = await Promise.all([
    requestResult(
      transaction.objectStore(PRIVATE_STORAGE_RECORD_STORE).getAll(),
    ) as Promise<PrivateRecord[]>,
    completed,
  ]);
  await Promise.all(
    records
      .filter(
        (record) =>
          record.id.startsWith(prefix) && record.expiresAt <= Date.now(),
      )
      .map((record) => deletePrivateValue(record.id)),
  );
}
